/* oxlint-disable react/react-compiler -- Existing code is not yet compatible with React Compiler checks. */
import * as React from 'react';

import {
    type Block,
    type Document,
    type Inline,
    Leaf,
    type Text,
    type TextRange,
} from '@gitbook/slate';
import type { List } from '@gitbook/util-immutable';

import type { EditorContainer, SelectionProps } from '../interfaces';
import LeafRenderer from './LeafRenderer';

interface TextRendererProps {
    block: Block;
    decorations: List<TextRange>;
    selections: List<TextRange>;
    editor: EditorContainer;
    node: Text;
    parent: Inline | Block;
    placeholder?: string;
    horizontalAlignment?: 'left' | 'center' | 'right';
}

function isTextRangeOnNode(key: string, range: TextRange, document: Document) {
    const { startKey, endKey } = range;

    if (startKey === key || endKey === key) {
        return true;
    }

    if (startKey === endKey) {
        return false;
    }

    const startsBefore = startKey && document.areDescendantsSorted(startKey, key);
    if (!startsBefore) {
        return false;
    }

    return endKey && document.areDescendantsSorted(key, endKey);
}

/*
 * Component to render a text node and all its leaves.
 */
const TextRenderer = React.memo(function MemoTextRenderer(
    props: TextRendererProps
): React.ReactElement {
    const {
        block,
        decorations,
        selections,
        editor,
        node,
        parent,
        placeholder,
        horizontalAlignment,
    } = props;
    const { value, stack } = editor;
    const { document } = value;
    const { key } = node;

    const filteredDecorations = decorations.filter((decoration) =>
        isTextRangeOnNode(key, decoration, document)
    );
    const filteredSelections = selections.filter((selection) =>
        isTextRangeOnNode(key, selection, document)
    );

    // PERF: Take advantage of cache by avoiding arguments
    const leavesAndSelections =
        (filteredDecorations.size === 0 && filteredSelections.size === 0) || parent.isVoid
            ? node.getLeaves()
            : node.getLeavesAndSelections(filteredDecorations, filteredSelections);

    // Filter leaves only for leaf rendering computations
    const leaves = leavesAndSelections.filter(Leaf.isLeaf) as List<Leaf>;

    let leafIndex = 0;
    let offset = 0;
    let rangeIndex = 0;
    const children = leavesAndSelections.toArray().map((item) => {
        let child;
        switch (item.object) {
            // Render a text leaf
            case 'leaf':
                {
                    child = (
                        <LeafRenderer
                            key={`${node.key}-leaf:${leafIndex}`}
                            block={block}
                            editor={editor}
                            index={leafIndex}
                            marks={item.marks}
                            selections={item.selections}
                            node={node}
                            offset={offset}
                            parent={parent}
                            leaves={leaves}
                            text={item.text}
                            // @ts-expect-error
                            placeholder={placeholder}
                            horizontalAlignment={horizontalAlignment}
                        />
                    );

                    offset += item.text.length;
                    leafIndex++;
                }
                break;
            // Render a selection
            case 'range':
                {
                    const selectionProps: SelectionProps = {
                        type: 'focus',
                        selection: item,
                    };
                    child = (
                        <React.Fragment key={`${node.key}-range:${rangeIndex}`}>
                            {stack.find('renderSelection', selectionProps)}
                        </React.Fragment>
                    );

                    rangeIndex++;
                }
                break;
        }

        return child;
    });

    return <span data-key={key}>{children}</span>;
}, areEqual);

/*
 * Should the text avoid updating?
 */
function areEqual(p: TextRendererProps, n: TextRendererProps): boolean {
    // If the Editor reference has changed, update.
    if (n.editor !== p.editor) {
        return false;
    }

    // If the node has changed, update. PERF: There are cases where it will have
    // changed, but it's properties will be exactly the same (eg. copy-paste)
    // which this won't catch. But that's rare and not a drag on performance, so
    // for simplicity we just let them through.
    if (n.node !== p.node) {
        return false;
    }

    // If the node parent is a block node, and it was the last child of the
    // block, re-render to cleanup extra `\n`.
    if (n.parent.object === 'block') {
        const pLast = p.parent.nodes.last();
        const nLast = n.parent.nodes.last();
        if (p.node === pLast && n.node !== nLast) {
            return false;
        }
    }

    // Re-render if the current decorations have changed.
    if (!n.decorations.equals(p.decorations)) {
        return false;
    }

    // Re-render if the current decorations have changed.
    if (!n.selections.equals(p.selections)) {
        return false;
    }

    // Otherwise, don't update.
    return true;
}

export default TextRenderer;
